import React from 'react';
import { Hourglass } from 'lucide-react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Text } from '@cotera/client/app/components/ui';
import { useUpstreamSourceLabels } from '../../../hooks/use-upstream-source-labels';
import type {
  WorkflowNodeRenderStrategy,
  WorkflowNodeViewData,
} from '../types';
import { asRecord } from '../../utils';
import { describeFieldPath } from './describe-field-path';

const { WorkflowNodeType, DEBOUNCE_WINDOW_LABELS, parseDebounceConfig } =
  PersistedWorkflow;

/**
 * Card body — a component so it can resolve friendly source names via hooks.
 *
 * Reads `once a week per /subscriptionId`, matching `describeDebounceConfig`
 * but with the key path rendered through the shared label resolver so a
 * `Node/field` key shows the same name the picker showed.
 */
const DebounceNodeBody: React.FC<{ node: WorkflowNodeViewData }> = ({
  node,
}) => {
  const sourceLabels = useUpstreamSourceLabels();
  const parsed = parseDebounceConfig(asRecord(node.data));
  if (!parsed.ok) {
    return (
      <Text.Caption className="text-muted-foreground block truncate">
        No key configured
      </Text.Caption>
    );
  }
  const { config } = parsed;
  return (
    <Text.Caption className="text-muted-foreground block truncate">
      {`once ${DEBOUNCE_WINDOW_LABELS[config.window]} per ${describeFieldPath(
        config.keyPath,
        sourceLabels
      )}`}
    </Text.Caption>
  );
};

export const debounceNodeStrategy: WorkflowNodeRenderStrategy = {
  nodeType: WorkflowNodeType.Debounce,
  accent: 'control',
  badge: 'Debounce',
  getTitle: () => 'Debounce',
  getDescription: () => 'Continue once per time window for each key',
  renderIcon: () => <Hourglass className="h-4 w-4" />,
  renderBody: (node) => <DebounceNodeBody node={node} />,
};
